import { Injectable } from '@angular/core';

import { RecordingSessionService } from './recording/recording-session.service';

/**
 * Hashed scripts stay cached for a year. The HTML shell must be fetched again after each deploy or the
 * browser keeps running the previous scripts. Safari and the iOS web view often ignore Cache-Control on
 * the document, so this compares the shell on the server with the scripts already loaded.
 * One request per page load. A tab left open keeps its current scripts until the next load.
 * A bundled Capacitor app (capacitor://) already has the scripts that shipped with that build, so a reload
 * cannot pick up a new deploy. Hosted and ng-serve WebViews still compare the shell.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly reloadParam = 'scryber_build';
  private readonly attemptKey = 'scryber_update_attempt';

  constructor(private readonly recordingSession: RecordingSessionService) {}

  start(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    this.stripReloadParam();
    void this.check();
  }

  private stripReloadParam(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(this.reloadParam)) {
      return;
    }
    url.searchParams.delete(this.reloadParam);
    const next = url.pathname + url.search + url.hash;
    window.history.replaceState(null, '', next);
  }

  private async check(): Promise<void> {
    if (window.location.protocol === 'capacitor:') {
      return;
    }
    if (this.recordingSession.isCaptureActive()) {
      return;
    }
    try {
      const response = await fetch(`/index.html?${this.reloadParam}=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) {
        return;
      }
      const html = await response.text();
      const latest = scriptPathsFromHtml(html);
      const current = scriptPathsFromDocument();
      if (!latest.length || samePaths(current, latest)) {
        window.sessionStorage.removeItem(this.attemptKey);
        return;
      }
      const fingerprint = latest.join('|');
      if (window.sessionStorage.getItem(this.attemptKey) === fingerprint) {
        return;
      }
      window.sessionStorage.setItem(this.attemptKey, fingerprint);
      const url = new URL(window.location.href);
      url.searchParams.set(this.reloadParam, String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      // Offline or a blocked fetch should keep the page the user already has.
    }
  }
}

function scriptPathsFromDocument(): string[] {
  return Array.from(document.querySelectorAll('script[src]'))
    .map((node) => node.getAttribute('src') ?? '')
    .map(normalizeScriptPath)
    .filter((path) => path.endsWith('.js'))
    .sort();
}

function scriptPathsFromHtml(html: string): string[] {
  const paths: string[] = [];
  const pattern = /<script[^>]+src=["']([^"']+)["']/gi;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(html)) !== null) {
    const path = normalizeScriptPath(match[1]);
    if (path.endsWith('.js')) {
      paths.push(path);
    }
  }
  return paths.sort();
}

function normalizeScriptPath(src: string): string {
  try {
    return new URL(src, window.location.origin).pathname;
  } catch {
    return src.split('?')[0];
  }
}

function samePaths(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((path, index) => path === right[index]);
}
